import { useState, useRef, useEffect } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Camera, Trash2 } from "lucide-react";

// photoUrl comes from backend as "/api/players/{id}/photo"
// axios baseURL already includes "/api", so we strip the leading "/api" when fetching.
const stripApi = (url) => (url || "").replace(/^\/api/, "");

export default function PhotoUpload({ playerId, userId, photoUrl, name, size = "lg", onChange }) {
  const [busy, setBusy] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const inputRef = useRef(null);
  const targetId = userId || playerId;
  const endpoint = userId ? `/users/${userId}/photo` : `/players/${playerId}/photo`;

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    if (photoUrl) {
      api.get(stripApi(photoUrl), { responseType: "blob" })
        .then((r) => {
          if (cancelled) return;
          createdUrl = URL.createObjectURL(r.data);
          setBlobUrl(createdUrl);
        })
        .catch(() => setBlobUrl(null));
    } else {
      setBlobUrl(null);
    }
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [photoUrl]);

  const submit = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Imagem máxima de 3MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Fotografia atualizada");
      onChange && onChange();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = async () => {
    if (!window.confirm("Remover fotografia?")) return;
    setBusy(true);
    try {
      await api.delete(endpoint);
      toast.success("Fotografia removida");
      onChange && onChange();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const sizes = {
    sm: { box: 40, icon: 14 },
    md: { box: 64, icon: 18 },
    lg: { box: 120, icon: 28 },
    xl: { box: 160, icon: 36 },
  };
  const s = sizes[size] || sizes.lg;
  const initials = (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="inline-flex items-center gap-3" data-testid="photo-upload">
      <div className="relative">
        <div
          className="overflow-hidden border-2 border-[#E50000]"
          style={{ width: s.box, height: s.box, borderRadius: "2px", background: "#1A1A1A" }}
        >
          {blobUrl ? (
            <img src={blobUrl} alt={name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-display font-black text-[#737373]"
              style={{ fontSize: s.box / 3 }}>
              {initials}
            </div>
          )}
        </div>
      </div>
      {targetId && (
        <div className="flex flex-col gap-1">
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
            onChange={(e) => submit(e.target.files?.[0])} data-testid="photo-input" />
          <button onClick={() => inputRef.current?.click()} disabled={busy}
            className="kw-btn-secondary !py-1 !px-3 inline-flex items-center gap-1 text-xs"
            data-testid="photo-upload-btn">
            <Camera className="w-3 h-3" /> {busy ? "..." : photoUrl ? "Mudar" : "Carregar"}
          </button>
          {photoUrl && (
            <button onClick={remove} disabled={busy}
              className="text-[#FF6B6B] hover:text-white text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1"
              data-testid="photo-remove-btn">
              <Trash2 className="w-3 h-3" /> Remover
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function usePhotoBlob(photoUrl) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let created = null;
    setBlobUrl(null);
    if (photoUrl) {
      api.get(stripApi(photoUrl), { responseType: "blob" })
        .then((r) => {
          if (cancelled) return;
          created = URL.createObjectURL(r.data);
          setBlobUrl(created);
        })
        .catch(() => {});
    }
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created); };
  }, [photoUrl]);
  return blobUrl;
}

export function Avatar({ photoUrl, name, size = 40 }) {
  const blobUrl = usePhotoBlob(photoUrl);

  const initials = (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="inline-block overflow-hidden border border-[#333]"
      style={{ width: size, height: size, borderRadius: "2px", background: "#1A1A1A" }}>
      {blobUrl ? (
        <img src={blobUrl} alt={name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center font-display font-black text-[#737373]"
          style={{ fontSize: size / 2.8 }}>{initials}</div>
      )}
    </div>
  );
}
